// ============================================================================
// Graph to Code Modifier
// ============================================================================

import { toPythonicIdentifier } from '@polyai/stream/utils/stringHelper';

import type { Condition, Flow } from '../../types';
import { blockIdentifier } from '../parser/block-identifier';
import { commentLine, uncommentLine } from '../parser/regex-parser';
import type { ConditionBlockBoundary } from '../types';

/**
 * Safe code modifier that only modifies condition blocks
 */
export class SafeCodeModifier {
  /**
   * Comments out a condition block in the code
   * @param {string} code - The Python code to modify
   * @param {Condition} condition - The condition to comment out
   * @param {Flow} flow - The flow containing all steps
   * @return {string} Updated Python code with the condition block commented out
   */
  commentOutConditionBlock(
    code: string,
    condition: Condition,
    flow: Flow,
  ): string {
    const block = this.findBestMatchingBlock(
      blockIdentifier.findConditionBlocks(code),
      condition,
      flow,
    );

    if (!block) {
      return code;
    }

    const lines = code.split('\n');
    return this.commentOutActionLine(lines, block).join('\n');
  }

  /**
   * Uncomments a condition block in the code
   * @param {string} code - The Python code to modify
   * @param {Condition} condition - The condition to uncomment
   * @param {Flow} flow - The flow containing all steps
   * @return {string} Updated Python code with the condition block uncommented
   */
  uncommentConditionBlock(
    code: string,
    condition: Condition,
    flow: Flow,
  ): string {
    const commentedBlocks = blockIdentifier
      .findConditionBlocks(code)
      .filter(b => b.isCommented);
    const block = this.findBestMatchingBlock(commentedBlocks, condition, flow);

    if (!block) {
      return code;
    }

    const lines = code.split('\n');
    const result: string[] = [];

    for (let i = 0; i < lines.length; i++) {
      if (i >= block.startLine && i <= block.endLine) {
        result.push(uncommentLine(lines[i]));
      } else {
        result.push(lines[i]);
      }
    }

    return result.join('\n');
  }

  /**
   * Updates step name references ONLY within identified condition blocks
   * @param {string} code - The Python code to modify
   * @param {string} oldStepName - The old step name to replace
   * @param {string} newStepName - The new step name to use
   * @return {string} Updated Python code with step names updated in condition blocks
   */
  updateStepNameInConditionBlocks(
    code: string,
    oldStepName: string,
    newStepName: string,
  ): string {
    try {
      const blocks = blockIdentifier.findConditionBlocks(code);
      const lines = code.split('\n');
      const result: string[] = [...lines];

      // Only modify goto_step calls within identified condition blocks
      for (const block of blocks) {
        if (!block.stepName) {
          continue;
        }

        // Only update if this block references the old step name
        if (block.stepName !== oldStepName) {
          continue;
        }

        // Update the step name in the goto_step call using regex
        const lineNum = block.endLine;
        if (lineNum >= lines.length) {
          continue;
        }

        const originalLine = lines[lineNum];
        const escapedOldStepName = oldStepName.replace(
          /[.*+?^${}()|[\]\\]/g,
          '\\$&',
        );
        const escapedNewStepName = this.escapePythonString(newStepName);

        // Pattern 1: flow.goto_step("old_step", ...) or flow.goto_step('old_step', ...)
        let updatedLine = originalLine.replace(
          new RegExp(`flow\\.goto_step\\(["']${escapedOldStepName}["']`, 'g'),
          `flow.goto_step(${escapedNewStepName}`,
        );

        // Pattern 2: flow.goto_step(..., step="old_step", ...) or step='old_step'
        updatedLine = updatedLine.replace(
          new RegExp(`step\\s*=\\s*["']${escapedOldStepName}["']`, 'g'),
          `step=${escapedNewStepName}`,
        );

        result[lineNum] = updatedLine;
      }

      return result.join('\n');
    } catch (error) {
      console.error('Failed to update step name in code:', error);
      return code;
    }
  }

  /**
   * Updates condition label in code
   * @param {string} code - The Python code to modify
   * @param {Condition} condition - The condition to update
   * @param {string} oldLabel - The old label to replace
   * @param {string} newLabel - The new label to use
   * @param {Flow} flow - The flow containing all steps
   * @return {string} Updated Python code with the condition label updated
   */
  updateConditionLabel(
    code: string,
    condition: Condition,
    oldLabel: string,
    newLabel: string,
    flow: Flow,
  ): string {
    const blocks = blockIdentifier.findConditionBlocks(code);

    // For step conditions, find the child step name to match ALL blocks routing to that step
    let childStepName: string | undefined;
    if (condition.config.$case !== 'exitFlowCondition') {
      const childStepId = condition.config.value.childStepId;
      const childStep = flow.steps.entities[childStepId];
      if (childStep) {
        childStepName = childStep.name;
      }
    }

    // Find ALL matching blocks (not just the first one)
    const matchingBlocks = childStepName
      ? blocks.filter(b => !b.isExitFlow && b.stepName === childStepName)
      : blocks.filter(b => this.matchesCondition(b, condition, flow));

    if (matchingBlocks.length === 0) {
      return code;
    }

    try {
      const lines = code.split('\n');
      const result = [...lines];
      const escapedOldLabel = oldLabel.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      const escapedNewLabel = this.escapePythonString(newLabel);

      // Process in reverse order to avoid line number drift
      const sortedBlocks = [...matchingBlocks].sort(
        (a, b) => b.endLine - a.endLine,
      );

      for (const block of sortedBlocks) {
        const lineNum = block.endLine;

        if (lineNum >= lines.length) {
          continue;
        }

        const originalLine = result[lineNum];

        // Pattern 1: flow.goto_step(..., "old_label") or flow.goto_step(..., 'old_label')
        let updatedLine = originalLine.replace(
          new RegExp(`,\\s*["']${escapedOldLabel}["']\\s*\\)`, 'g'),
          `, ${escapedNewLabel})`,
        );

        // Pattern 2: flow.goto_step(..., label="old_label") or label='old_label'
        updatedLine = updatedLine.replace(
          new RegExp(`label\\s*=\\s*["']${escapedOldLabel}["']`, 'g'),
          `label=${escapedNewLabel}`,
        );

        result[lineNum] = updatedLine;
      }

      return result.join('\n');
    } catch (error) {
      console.error('Failed to update condition label:', error);
      return code;
    }
  }

  /**
   * Updates condition connection (childStepId) in code
   * @param {string} code - The Python code to modify
   * @param {string} oldStepName - The old step name to replace
   * @param {string} newStepName - The new step name to use
   * @return {string} Updated Python code with condition connections updated
   */
  updateConditionConnection(
    code: string,
    oldStepName: string,
    newStepName: string,
  ): string {
    // First update the step name in the goto_step call
    const updatedCode = this.updateStepNameInConditionBlocks(
      code,
      oldStepName,
      newStepName,
    );

    return updatedCode;
  }

  /**
   * Comments out all conditions referencing a deleted step
   * @param {string} code - The Python code to modify
   * @param {string} deletedStepId - The ID of the deleted step
   * @param {Flow} flow - The flow containing all steps
   * @return {string} Updated Python code with conditions referencing the deleted step commented out
   */
  commentOutConditionsReferencingStep(
    code: string,
    deletedStepId: string,
    flow: Flow,
  ): string {
    const deletedStep = flow.steps.entities[deletedStepId];
    if (!deletedStep) {
      return code;
    }

    const deletedStepName = deletedStep.name;
    const blocks = blockIdentifier.findConditionBlocks(code);

    // Find all blocks referencing the deleted step
    // Process in reverse order to avoid line number drift from pass insertions
    const matchingBlocks = blocks.filter(b => b.stepName === deletedStepName);
    matchingBlocks.sort((a, b) => b.endLine - a.endLine);

    let lines = code.split('\n');
    for (const block of matchingBlocks) {
      lines = this.commentOutActionLine(lines, block);
    }

    return lines.join('\n');
  }

  /**
   * Comments out only the action line (goto_step/exit_flow) in a condition block,
   * keeping the variable assignment and if statement intact.
   * Adds `pass` if needed to keep the if block valid Python.
   * For bare blocks at the function body level, skip pass insertion.
   */
  // biome-ignore lint/complexity/noExcessiveCognitiveComplexity: existing
  private commentOutActionLine(
    lines: string[],
    block: ConditionBlockBoundary,
  ): string[] {
    const result = [...lines];

    // Comment out only the goto_step/exit_flow line (endLine)
    result[block.endLine] = commentLine(lines[block.endLine]);

    // For bare blocks (single line), check if inside a block that needs pass.
    // If not inside any block (function body level), skip pass insertion.
    if (block.startLine === block.endLine) {
      const actionLine = lines[block.endLine];
      const actionIndent = actionLine.match(/^(\s*)/)?.[1]?.length || 0;
      let insideBlock = false;
      for (let i = block.endLine - 1; i >= 0; i--) {
        const prevTrimmed = lines[i].trim();
        if (prevTrimmed === '') {
          continue;
        }
        const prevIndent = lines[i].match(/^(\s*)/)?.[1]?.length || 0;
        if (prevIndent < actionIndent) {
          if (
            prevTrimmed.endsWith(':') &&
            !/^(?:def |class )/.test(prevTrimmed)
          ) {
            insideBlock = true;
          }
          break;
        }
      }
      if (!insideBlock) {
        return result;
      }
    }

    // Get the indentation of the action line for the pass statement
    const actionLine = lines[block.endLine];
    const indent = actionLine.match(/^(\s*)/)?.[1] || '';

    // Check if pass already exists right after the action line within the if block
    let hasPass = false;
    for (let i = block.endLine + 1; i < lines.length; i++) {
      const trimmed = lines[i].trim();
      if (trimmed === '') {
        continue;
      }
      const lineIndent = lines[i].match(/^(\s*)/)?.[1]?.length || 0;
      if (lineIndent < indent.length) {
        break; // exited the if block
      }
      if (trimmed === 'pass') {
        hasPass = true;
      }
      break; // only check the first non-empty line
    }

    if (!hasPass) {
      result.splice(block.endLine + 1, 0, `${indent}pass`);
    }

    return result;
  }

  /**
   * Finds the best matching block for a condition, preferring strong matches
   * (variable name + step name) over weak matches (step name only).
   * This prevents targeting the wrong block when multiple blocks route to the same step.
   */
  private findBestMatchingBlock(
    blocks: ConditionBlockBoundary[],
    condition: Condition,
    flow: Flow,
  ): ConditionBlockBoundary | undefined {
    // Try strong match first (variable name + step name)
    if (condition.config.$case !== 'exitFlowCondition') {
      const variableName = toPythonicIdentifier(
        condition.config.value.details.label,
      );
      const childStepId = condition.config.value.childStepId;
      const childStep = flow.steps.entities[childStepId];

      if (childStep) {
        const strongMatch = blocks.find(
          b =>
            !b.isExitFlow &&
            b.variableName === variableName &&
            b.stepName === childStep.name,
        );
        if (strongMatch) {
          return strongMatch;
        }
      }
    }

    // Fall back to any match
    return blocks.find(b => this.matchesCondition(b, condition, flow));
  }

  /**
   * Checks if a block matches a condition
   * For step conditions, matches by variable name + step name (strong match),
   * falling back to step name only (weak match for backward compat)
   * For exit flow conditions, matches by variable name derived from label
   * @param {ConditionBlockBoundary} block - The condition block boundary to check
   * @param {Condition} condition - The condition to match against
   * @param {Flow} flow - The flow containing all steps
   * @return {boolean} True if the block matches the condition, false otherwise
   */
  private matchesCondition(
    block: ConditionBlockBoundary,
    condition: Condition,
    flow: Flow,
  ): boolean {
    // Handle exit flow conditions - match by variable name and exit flow flag
    if (condition.config.$case === 'exitFlowCondition') {
      const variableName = toPythonicIdentifier(
        condition.config.value.details.label,
      );
      return block.isExitFlow && block.variableName === variableName;
    }

    // For step conditions, use variable name + step name for precise matching
    const variableName = toPythonicIdentifier(
      condition.config.value.details.label,
    );
    const childStepId = condition.config.value.childStepId;
    const childStep = flow.steps.entities[childStepId];

    if (!childStep) {
      // If step doesn't exist, fall back to variable name matching
      return !block.isExitFlow && block.variableName === variableName;
    }

    // Strong match: variable name + step name
    if (
      block.variableName === variableName &&
      block.stepName === childStep.name
    ) {
      return true;
    }

    // Weak match: step name only (backward compat — when label was previously
    // changed on graph, the code's variable name won't match the new label)
    return !block.isExitFlow && block.stepName === childStep.name;
  }

  /**
   * Escapes a string for use in Python code (wraps in quotes)
   * @param {string} str - The string to escape
   * @return {string} Escaped string wrapped in quotes
   */
  private escapePythonString(str: string): string {
    if (str.includes("'")) {
      return `"${str.replace(/"/g, '\\"')}"`;
    }
    // eslint-disable-next-line no-useless-escape
    return `'${str.replace(/'/g, "\\'")}'`;
  }
}

// Export singleton instance
export const codeModifier = new SafeCodeModifier();
